import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import WhatsAppButton from './WhatsappButton';

type FormType = 'poc' | 'guaranteed' | 'general';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  industryOther?: string;
  projectDescription: string;
  budget?: string;
  estimatedCost?: string;
  meetingDateTime?: string;
  timeline?: string;
  bottleneck?: string;
  expectedResults?: string;
  preferredDateTime?: string;
  mainChallenge?: string;
  currentProcess?: string;
  teamSize?: string;
}

const Contact: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeForm, setActiveForm] = useState<FormType>('general');
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    industryOther: '',
    projectDescription: '',
    budget: '',
    estimatedCost: '',
    meetingDateTime: '',
    timeline: '',
    bottleneck: '',
    expectedResults: '',
    preferredDateTime: '',
    mainChallenge: '',
    currentProcess: '',
    teamSize: ''
  });

  useEffect(() => {
    const hash = location.hash.replace('#', '') as FormType;
    if (hash && ['poc', 'guaranteed', 'general'].includes(hash)) {
      setActiveForm(hash);
    }
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);

    // Prepare base data (common fields)
    const baseData = {
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      industry: formData.industry,
      ...(formData.industry === 'other' && { industryOther: formData.industryOther }),
      formType: activeForm,
      timestamp: new Date().toISOString()
    };

    // Add form-specific fields based on activeForm type
    let submissionData;

    if (activeForm === 'poc') {
      // Discovery Meeting form
      submissionData = {
        ...baseData,
        preferredDateTime: formData.preferredDateTime,
        mainChallenge: formData.mainChallenge,
        currentProcess: formData.currentProcess
      };
    } else if (activeForm === 'guaranteed') {
      // Detailed Project Scoping form
      submissionData = {
        ...baseData,
        estimatedCost: formData.estimatedCost,
        projectDescription: formData.projectDescription,
        timeline: formData.timeline,
        meetingDateTime: formData.meetingDateTime,
        expectedResults: formData.expectedResults
      };
    } else {
      // General inquiry form
      submissionData = {
        ...baseData,
        projectDescription: formData.projectDescription
      };
    }

    try {
      // Send to n8n webhook
      await fetch('https://n8n.m-kaos.com/webhook/kairos-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });

      console.log('Form submitted successfully:', submissionData);

      // Show success popup
      setShowPopup(true);

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        industryOther: '',
        projectDescription: '',
        budget: '',
        estimatedCost: '',
        meetingDateTime: '',
        timeline: '',
        bottleneck: '',
        expectedResults: '',
        preferredDateTime: '',
        mainChallenge: '',
        currentProcess: '',
        teamSize: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      // Still show success popup even if webhook fails
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    navigate('/');
  };

  const renderCommonFields = () => (
    <>
      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.fullName} {t.contact.form.required}
        </label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.email} {t.contact.form.required}
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.phone} {t.contact.form.required}
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.company} {t.contact.form.required}
        </label>
        <input
          type="text"
          name="company"
          value={formData.company}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      </div>

      <div className={formData.industry === 'other' ? '' : 'md:col-span-1'}>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.industry} {t.contact.form.required}
        </label>
        <select
          name="industry"
          value={formData.industry}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <option value="" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.form.industryPlaceholder}</option>
          <option value="real-estate" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.realEstate}</option>
          <option value="marketing" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.marketing}</option>
          <option value="consulting" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.consulting}</option>
          <option value="ecommerce" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.ecommerce}</option>
          <option value="healthcare" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.healthcare}</option>
          <option value="finance" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.finance}</option>
          <option value="technology" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.technology}</option>
          <option value="other" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.industries.other}</option>
        </select>
      </div>

      {formData.industry === 'other' && (
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            {t.contact.form.industryOther} {t.contact.form.required}
          </label>
          <input
            type="text"
            name="industryOther"
            value={formData.industryOther}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 rounded-lg glass-strong"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: 'var(--color-text-primary)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            placeholder={t.contact.form.industryOtherPlaceholder}
          />
        </div>
      )}
    </>
  );

  const renderPOCForm = () => (
    <>
      {renderCommonFields()}

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.preferredDateTime} {t.contact.form.required}
        </label>
        <input
          type="datetime-local"
          name="preferredDateTime"
          value={formData.preferredDateTime}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.mainChallenge} {t.contact.form.required}
        </label>
        <textarea
          name="mainChallenge"
          value={formData.mainChallenge}
          onChange={handleInputChange}
          required
          rows={3}
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          placeholder={t.contact.form.mainChallengePlaceholder}
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.currentProcess}
        </label>
        <textarea
          name="currentProcess"
          value={formData.currentProcess}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          placeholder={t.contact.form.currentProcessPlaceholder}
        />
      </div>
    </>
  );

  const renderGuaranteedForm = () => (
    <>
      {renderCommonFields()}

      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.estimatedCost} {t.contact.form.required}
        </label>
        <input
          type="number"
          name="estimatedCost"
          value={formData.estimatedCost}
          onChange={handleInputChange}
          required
          min="0"
          step="1"
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          placeholder={t.contact.form.estimatedCostPlaceholder}
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.projectDescription} {t.contact.form.required}
        </label>
        <textarea
          name="projectDescription"
          value={formData.projectDescription}
          onChange={handleInputChange}
          required
          rows={4}
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          placeholder={t.contact.form.projectDescriptionPlaceholder}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.timeline}
        </label>
        <select
          name="timeline"
          value={formData.timeline}
          onChange={handleInputChange}
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <option value="" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.form.timelinePlaceholder}</option>
          <option value="urgent" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.form.timelineOptions.urgent}</option>
          <option value="soon" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.form.timelineOptions.soon}</option>
          <option value="flexible" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.form.timelineOptions.flexible}</option>
          <option value="planning" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{t.contact.form.timelineOptions.planning}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.meetingDateTime} {t.contact.form.required}
        </label>
        <input
          type="datetime-local"
          name="meetingDateTime"
          value={formData.meetingDateTime}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.expectedResults} {t.contact.form.required}
        </label>
        <textarea
          name="expectedResults"
          value={formData.expectedResults}
          onChange={handleInputChange}
          required
          rows={3}
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          placeholder={t.contact.form.expectedResultsPlaceholder}
        />
      </div>
    </>
  );

  const renderGeneralForm = () => (
    <>
      {renderCommonFields()}

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {t.contact.form.message} {t.contact.form.required}
        </label>
        <textarea
          name="projectDescription"
          value={formData.projectDescription}
          onChange={handleInputChange}
          required
          rows={5}
          className="w-full px-4 py-3 rounded-lg glass-strong"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          placeholder={t.contact.form.messagePlaceholder}
        />
      </div>
    </>
  );

  const getFormTitle = () => {
    return t.contact.titles[activeForm];
  };

  const getFormDescription = () => {
    return t.contact.descriptions[activeForm];
  };

  return (
    <section className="py-20 px-6 relative min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at top, rgba(99, 102, 241, 0.3), transparent 70%), radial-gradient(ellipse at bottom, rgba(236, 72, 153, 0.3), transparent 70%)', filter: 'blur(80px)' }}></div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          <button
            onClick={() => setActiveForm('poc')}
            className="px-6 py-3 rounded-lg font-semibold transition-all duration-300"
            style={{
              backgroundColor: activeForm === 'poc' ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.05)',
              color: activeForm === 'poc' ? 'white' : 'var(--color-text-secondary)',
              boxShadow: activeForm === 'poc' ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
            }}
          >
            {t.contact.tabs.poc}
          </button>

          <button
            onClick={() => setActiveForm('guaranteed')}
            className="px-6 py-3 rounded-lg font-semibold transition-all duration-300"
            style={{
              backgroundColor: activeForm === 'guaranteed' ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.05)',
              color: activeForm === 'guaranteed' ? 'white' : 'var(--color-text-secondary)',
              boxShadow: activeForm === 'guaranteed' ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
            }}
          >
            {t.contact.tabs.guaranteed}
          </button>

          <button
            onClick={() => setActiveForm('general')}
            className="px-6 py-3 rounded-lg font-semibold transition-all duration-300"
            style={{
              backgroundColor: activeForm === 'general' ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.05)',
              color: activeForm === 'general' ? 'white' : 'var(--color-text-secondary)',
              boxShadow: activeForm === 'general' ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
            }}
          >
            {t.contact.tabs.general}
          </button>
        </motion.div>

        <WhatsAppButton />

        {/* Form Header */}
        <div className="text-center mb-12">
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {getFormTitle()}
          </h1>
          <p
            className="text-xl"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {getFormDescription()}
          </p>
        </div>

        {/* Form */}
        <motion.form
          key={activeForm}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          onSubmit={handleSubmit}
          className="glass-strong p-4 sm:p-6 md:p-8 rounded-3xl"
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.2)'
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {activeForm === 'poc' && renderPOCForm()}
            {activeForm === 'guaranteed' && renderGuaranteedForm()}
            {activeForm === 'general' && renderGeneralForm()}
          </div>

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
            className="w-full mt-8 py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--color-primary)',
              color: 'white'
            }}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t.common.loading}
              </span>
            ) : (
              t.contact.form.submit
            )}
          </motion.button>
        </motion.form>
      </div>

      {/* Success Popup */}
      {showPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={closePopup}
        >
          <motion.div
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong p-8 rounded-3xl max-w-md w-full text-center"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
            }}
          >
            <div className="text-6xl mb-4">✅</div>
            <h3
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {t.contact.popup.title}
            </h3>
            <p
              className="text-lg mb-6"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t.contact.popup.message}
            </p>
            <p
              className="text-sm mb-6"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t.contact.popup.details}
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={closePopup}
              className="px-8 py-3 rounded-xl font-bold"
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'white'
              }}
            >
              {t.contact.popup.button}
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default Contact;
